import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { getDefaultMessageResultsFound, getTranslateByKey } from "@/main/services/utils";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { toast } from "react-toastify";
import * as S from "./styles";
import { InsurerFilterRequest } from "@/main/models/request";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { insurerApi, supervisorApi } from "@/main/api";
import { SaveInsurerCompanyBySupervisor } from "@/main/models/request/supervisorRequest";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { IGetByInsurer } from "@/main/models/response";
import KOGCollapse from "../../kogCollapse/kogCollapse";
import { useUserReducer } from "@/main/store/user";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import KogInputTextControlledNew from "../../inputText/inputTextControlledNew";
import KOGInsurerCompanyBoxGridMultiCheckbox from "../insurerCompanyBoxGridMultiCheckbox/insurerCompanyBoxGridMultiCheckbox";
import { Box } from "@mui/material";

interface Props {
	open?: boolean;
	closeModal: () => void;
	guidSupervisor: string | null;
	loadGridList: () => void;
}

function InsurerCompanySidebar({ open, closeModal, guidSupervisor, loadGridList }: Props) {
	const INITIAL_FORM_STATE_SEARCH: {
		companyName: string;
		guidSupervisor: string;
		guidInsurer: string[];
	} = {
		companyName: "",
		guidSupervisor: "",
		guidInsurer: [],
	};

	type IInsurerCompanyFormSearch = typeof INITIAL_FORM_STATE_SEARCH;

	const {
		setValue,
		reset,
		handleSubmit,
		control,
		watch,
		setError,
		clearErrors,
		formState: { errors },
	} = useForm<IInsurerCompanyFormSearch>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE_SEARCH,
	});

	const [isLoading, setIsLoading] = useState(false);
	const [insurers, setInsurers] = useState<IGetByInsurer[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	useState<InsurerFilterRequest | null>(null);

	const titleGrid = getDefaultMessageResultsFound(insurers.length, totalRecords);

	const { currentUser } = useUserReducer();

	const searchInsurers = useCallback(
		async (pageIndex: number = PaginationEnum.PageIndexIntial) => {
			const guidCompany = currentUser?.idCompanyType === CompanyTypeEnum.Insurer ? currentUser?.guidCompany : null;

			const request: InsurerFilterRequest = {
				companyName: !guidCompany ? watch("companyName") : null,
				guidCompany: guidCompany,
				pageIndex: pageIndex,
				pageSize: PaginationEnum.PageSize15,
			};

			try {
				const response = await insurerApi.getByInsurer(request);
				const { results, totalNumberOfRecords } = response.data;

				if (pageIndex === 1) {
					setInsurers(results);
				} else {
					setInsurers((prev) => [...prev, ...results]);
				}

				setTotalRecords(totalNumberOfRecords);
				return results;
			} catch {
				return [];
			}
		},
		[currentUser, watch],
	);

	function onSearch() {
		const companyName = watch("companyName");
		if (!companyName || companyName.trim() === "") {
			setError("companyName", {
				type: "manual",
				message: getTranslateByKey("information.insurerRequired", true),
			});
			return;
		}
		clearErrors("companyName");
		setIsLoading(true);

		searchInsurers(PaginationEnum.PageIndexIntial).finally(() => {
			setIsLoading(false);
		});
	}

	const handleLoadMore = useCallback(
		async (pageIndex: number) => {
			const results = await searchInsurers(pageIndex);
			return results;
		},
		[searchInsurers],
	);

	async function onSubmitSave(request: SaveInsurerCompanyBySupervisor) {
		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setIsLoading(true);

		const newRequest: SaveInsurerCompanyBySupervisor = {
			guidSupervisor: guidSupervisor as string,
			guidInsurer: request.guidInsurer,
			changeJustification: {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			},
		};

		supervisorApi
			.saveInsurerCompanyBySupervisor(newRequest)
			.then(() => {
				toast.success(getTranslateByKey("information.savedSuccess"));
				closeModal();
				handleClear();
				loadGridList();
			})
			.catch(() => {})
			.finally(() => {
				setIsLoading(false);
			});
	}

	function handleBack() {
		handleClear();
	}

	function handleClear() {
		reset(INITIAL_FORM_STATE_SEARCH);
		setValue("companyName", "");
		setInsurers([]);
	}

	const rowHeaders = [getTranslateByKey("information.insurer")];

	return (
		<>
			{open && (
				<KogModalSideComponent
					open={open}
					closeModal={() => closeModal()}
					id="id-blacklist-modal-scroll"
					titleModalReducer={getTranslateByKey("information.registerInsurerScreen")}
					title={<S.Title>{getTranslateByKey("information.registerInsurerScreen")}</S.Title>}
				>
					<form onSubmit={handleSubmit(onSearch)}>
						<S.Container>
							<KogInputTextControlledNew
								control={control}
								id="id-insurer-name-input"
								required
								label={`${getTranslateByKey("information.insurer")}`}
								name="companyName"
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									setValue("companyName", e.target.value);
									if (e.target.value) clearErrors("companyName");
								}}
								error={!!errors.companyName}
								helperText={errors.companyName?.message}
								onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
									if (e.key === "Enter") {
										e.preventDefault();
										onSearch();
									}
								}}
							/>
							<S.AreaButtons>
								<SecondaryButton id="id-holiday-btn-back" onClick={handleBack}>
									{getTranslateByKey("information.clear")}
								</SecondaryButton>
								<SecondaryButton id="id-InsurerCompanyRegister-button-search" isLoading={isLoading} type="submit">
									{getTranslateByKey("information.search")}
								</SecondaryButton>
							</S.AreaButtons>
						</S.Container>
					</form>

					{insurers.length > 0 && (
						<S.Container style={{ marginTop: 20 }}>
							<KOGCollapse
								showBorder={false}
								header={
									<S.BodyBox style={{ position: "initial" }}>
										<S.Title>{getTranslateByKey("information.register")}</S.Title>
									</S.BodyBox>
								}
								content={
									<Box>
										<S.TitleGrid style={{ paddingLeft: 8, fontSize: 14, marginBottom: 0 }}>{titleGrid}</S.TitleGrid>
										<KOGInsurerCompanyBoxGridMultiCheckbox
											rowHeaders={rowHeaders}
											dataInsurersCompany={insurers}
											onSubmitSave={onSubmitSave}
											isLoading={isLoading}
											onLoadMore={handleLoadMore}
										/>
									</Box>
								}
								isOpenCollapseinitial={true}
							/>
						</S.Container>
					)}
				</KogModalSideComponent>
			)}
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default InsurerCompanySidebar;
